import sortBy from 'lodash/sortBy.js';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment';

import { handleResponseError, initialState } from '@/Helpers.js';
import AttendanceScenario, { OFFSITE_WITHOUT_INSTRUCTOR } from '@/models/AttendanceScenario.js';
import Model from '@/models/Model.js';

export default class Course extends Model {
    static entity = 'courses';

    static modelName = 'Course';

    static fields() {
        return {
            id: this.attr(null),
            active_student_count: this.attr(null),
            external_id: this.attr(null),
            course_settings_id: this.attr(null),
            course_template_id: this.attr(null),
            course_type_id: this.attr(null),
            grading_scheme_id: this.attr(null),
            grading_scheme: this.attr(null),
            course_key_id: this.attr(null),
            course_key_add_code: this.attr(null),
            title: this.attr(null),
            description: this.attr(null),
            is_active: this.boolean(null),
            completed: this.boolean(null),
            is_accessible: this.attr(null),
            is_accessible_to_students: this.attr(null),
            in_progress: this.attr(null),
            instructors: this.attr(null),
            has_gradable_activity: this.attr(null),
            has_course_grades: this.attr(null),
            log_entries_count: this.number(0),
            attendances_count: this.number(0),
            has_instructor: this.attr(null),
            course_codes: this.attr(null),
            prepended_course_code_title: this.attr(null),
            remediations: this.attr(null),
            remediation_activities: this.attr(null),
            has_activity_with_conflicting_minimum_passing_score: this.attr(null),
            points_possible: this.attr(null),
            points_possible_cache: this.attr(null),
            total_points_possible: this.attr(null),
            extra_credit_points: this.attr(null),
            total_remediation_points: this.attr(null),
            points_possible_checked_at: this.attr(null),
            total_points_possible_checked_at: this.attr(null),
            extra_credit_points_checked_at: this.attr(null),
            total_remediation_points_checked_at: this.attr(null),
            minimum_passing_criteria: this.attr(null),
            external_ids: this.attr(null),
            tags: this.attr([]),
            editing_override: this.attr(null),
            lock_status: this.boolean(false),
            current_lock_status: this.attr(null),
            current_lock_expires_at: this.attr(null),

            students: this.attr(null),

            ...super.fields(),

            sections: this.hasMany('sections', 'course_id'),
            course_settings: this.belongsTo('course_settings', 'course_settings_id'),
            discussion_threads: this.hasMany('discussion_threads', 'course_id'),
            announcements: this.hasMany('course_announcements', 'course_id'),
            meetings: this.hasMany('meetings', 'course_id'),
            groups: this.belongsToMany('groups', 'course_group', 'course_id', 'group_id'),
            log_entries: this.hasMany('course_log_entries', 'course_id'),
            gradebook: this.hasOne('gradebooks', 'course_id'),
            enrollments: this.hasMany('course_enrollments', 'course_id'),
            time_periods: this.hasMany('time_periods', 'course_id'),
            upcoming_time_periods: this.hasMany('time_periods', 'course_id'),
            enrollment_groups: this.hasMany('enrollment_groups', 'course_id'),

            course_template: this.belongsTo('course_templates', 'course_template_id'),
            // course_type: this.belongsTo(CourseType, 'course_type_id'),
            // grading_scheme: this.belongsTo(GradingScheme, 'grading_scheme_id'),
        };
    }

    get router_link() {
        return {
            name: 'course-dashboard',
            params: { courseId: this.id },
        };
    }

    get course_key_link() {
        if (!this.course_key_id) {
            return 'https://staging.thecoursekey.com/courses/';
        }

        if (initialState('CourseKey.debug')) {
            return `https://staging.thecoursekey.com/courses/${this.course_key_id}/attendance`;
        }

        return `https://portal.thecoursekey.com/courses/${this.course_key_id}/attendance`;
    }

    get gradebook_link() {
        return {
            name: 'gradebook-view',
            params: {
                courseId: this.id,
                gradebookId: this.gradebook.id,
            },
        };
    }

    get weeks() {
        return sortBy(
            this.sections.filter(section => ['week', 'break'].includes(section.section_settings?.type)),
            'section_settings.week_number'
        );
    }

    get weeks_without_breaks() {
        return sortBy(
            this.sections.filter(section => section.section_settings.type === 'week'),
            'section_settings.week_number'
        );
    }

    get has_flexible_attendance() {
        return this.course_settings?.has_flexible_attendance ?? false;
    }

    get show_student_enrollments() {
        return this.course_settings?.show_student_enrollments ?? false;
    }

    get permit_extra_credit_overflow() {
        return this.course_settings?.permit_extra_credit_overflow ?? false;
    }

    get course_key_enabled() {
        return this.course_settings?.course_key_enabled ?? false;
    }

    get scheduled_time() {
        return Course.scheduled_time(this.time_periods);
    }

    get ungrouped_scheduled_time() {
        return Course.scheduled_time(this.time_periods.filter(timePeriod => timePeriod.enrollment_groups.length === 0));
    }

    get manual_attendance() {
        if (!this.course_settings) {
            return false;
        }

        return !this.course_settings.course_key_enabled && !this.course_settings.exxat_course_id;
    }

    static scheduled_time(timePeriods) {
        const totals = {
            total_minutes: 0,
            total_break_minutes: 0,
            hours: 0,
            minutes: 0,
            break_hours: 0,
            break_minutes: 0,
        };

        // A cancelled session is not part of the schedule's requirements (Admin Story 13, AC-03), so it adds no time.
        timePeriods
            .filter(timePeriod => !timePeriod.is_cancelled)
            .forEach(timePeriod => {
                totals.total_minutes += timePeriod.scheduled_time.total_minutes;
                totals.total_break_minutes += timePeriod.scheduled_time.break_minutes;
            });

        totals.hours = Math.floor(totals.total_minutes / 60);
        totals.minutes = totals.total_minutes - totals.hours * 60;

        totals.break_hours = Math.floor(totals.total_break_minutes / 60);
        totals.break_minutes = totals.total_break_minutes - totals.break_hours * 60;

        return totals;
    }

    get is_offsite_without_instructor() {
        if (!this.course_settings) {
            return false;
        }

        const offsiteWithoutInstructor =
            AttendanceScenario.query().where('coursekey_attendance_name', OFFSITE_WITHOUT_INSTRUCTOR).first() ?? false;

        return this.course_settings.attendance_scenario_id === offsiteWithoutInstructor?.id;
    }

    get is_past_end_date() {
        if (!this.course_settings) {
            return false;
        }

        return moment().isAfter(this.course_settings.end_date);
    }

    static async getCourseById(courseId) {
        const endpoint = `/api/courses/${courseId}`;
        try {
            const { response } = await this.api().get(endpoint);
            return response;
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    static apiConfig = {
        actions: {
            fetchBasicInfo(id) {
                return this.get(`/api/courses/${id}?basic-information`);
            },
            // Backwards compatibility: preserve previous method name
            fetchById(id) {
                return this.get(`/api/courses/${id}?basic-information`);
            },
        },
    };
}
